import { useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

interface RegionCoordinate {
  provider: string;
  region: string;
  display_name: string;
  latitude: number;
  longitude: number;
  is_offered: boolean;
  utilization: number;
}

interface Props {
  coordinates: { [provider: string]: RegionCoordinate[] };
}

export default function QuotaMap({ coordinates }: Props) {
  // Fix for Leaflet icon issue in React
  useEffect(() => {
    // @ts-ignore
    delete (window as any).L?.Icon?.Default?.prototype?._getIconUrl;
  }, []);

  // Flatten coordinates for easier mapping
  const allRegions = Object.entries(coordinates).flatMap(([provider, regions]) =>
    regions.map(r => ({ ...r, provider }))
  );

  if (allRegions.length === 0) {
    return (
      <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
        <p className="text-gray-400">No region data available for map visualization</p>
      </div>
    );
  }

  const getMarkerColor = (utilization: number) => {
    if (utilization >= 90) return '#ef4444'; // red
    if (utilization >= 70) return '#eab308'; // yellow
    return '#10b981'; // green
  };

  const getMarkerSize = (isOffered: boolean) => {
    return isOffered ? 12 : 8;
  };

  const providerColors: Record<string, string> = {
    aws: '#ff9900',
    gcp: '#4285f4',
  };

  return (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
      <h3 className="text-lg font-semibold text-white mb-4">Global Region Distribution</h3>

      <div className="relative">
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '400px', borderRadius: '0.5rem' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="filter grayscale"
          />

          {allRegions.map((region, idx) => {
            const color = getMarkerColor(region.utilization);
            const size = getMarkerSize(region.is_offered);
            const providerColor = providerColors[region.provider] || '#666';

            return (
              <CircleMarker
                key={`${region.provider}-${region.region}-${idx}`}
                center={[region.latitude, region.longitude]}
                radius={size}
                pathOptions={{
                  fillColor: color,
                  color: providerColor,
                  weight: 2,
                  opacity: 1,
                  fillOpacity: 0.7
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold">{region.display_name}</div>
                    <div className="text-xs text-gray-600">
                      {region.provider.toUpperCase()} - {region.region}
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-xs">Utilization:</span>
                        <span className={`text-xs font-medium ${
                          region.utilization >= 90 ? 'text-red-500' :
                          region.utilization >= 70 ? 'text-yellow-500' :
                          'text-green-500'
                        }`}>
                          {region.utilization.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Status:</span>
                        <span className="text-xs">
                          {region.is_offered ? '✓ Offered' : 'Monitoring Only'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Custom dark overlay to match theme */}
        <div className="absolute inset-0 bg-gray-900/30 rounded-lg pointer-events-none" />
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center justify-between text-xs">
        <div className="flex items-center space-x-4">
          <span className="text-gray-400">Utilization:</span>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-gray-300">&lt;70%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-gray-300">70-90%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-gray-300">&gt;90%</span>
          </div>
        </div>

        <div className="flex items-center space-x-4 mt-2 md:mt-0">
          <span className="text-gray-400">Providers:</span>
          {Object.entries(providerColors).map(([provider, color]) => {
            const count = allRegions.filter(r => r.provider === provider).length;
            if (count === 0) return null;

            return (
              <div key={provider} className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 rounded-full border-2"
                  style={{ borderColor: color }}
                />
                <span className="text-gray-300">{provider.toUpperCase()} ({count})</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}